import styles from './RentalTopUpModal.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { ApiRentalDealItem } from 'src/types/rental';

import React, { useState } from 'react';
import { Warning, BankIcon, CheckCircle } from '@phosphor-icons/react';

import { depositLedgerOf } from 'src/utils/rentalClock';
import { readRentalPaymentToken } from 'src/utils/rentalOnchain';
import { rentalToken, formatBaseUnits } from 'src/utils/rentalInvoice';

import { useWalletUI, WalletButton } from 'src/blockchain';
import { useRentalTopUpQuote } from 'src/queries/useRentalClockQuotes';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

interface RentalTopUpModalProps {
  visible: boolean;
  onClose: () => void;
  rental: ApiRentalDealItem;
  chain: DealChain | null;
  /** Called after the on-chain topUpDeposit() succeeds so the screen can refetch. */
  onToppedUp?: () => void;
}

/**
 * Settles the rental in one payment (`topUpDeposit`, v2.2 §2.3): what is owed for overdue time
 * goes straight to the dealer, and the rest refills the deposit to its original amount. That is
 * what re-opens extension past the end date — `approveExtension()` reverts until the deposit is
 * whole AND nothing is owed.
 *
 * The amount is not the renter's choice and is not computed here: the contract rejects (never
 * clamps) anything above what it accepts, so the number comes from the server's quote of
 * `getMaxTopUp()`, freshly, every time the modal opens. Two wallet prompts: approve(amount) then
 * topUpDeposit(amount) — the same pattern and the same progress panel as every other signature.
 */
export const RentalTopUpModal: FC<RentalTopUpModalProps> = ({ visible, onClose, rental, chain, onToppedUp }) => {
  const walletUI = useWalletUI(chain?.family);
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const state = rental.rentalContractState;
  const token = rentalToken(rental, chain);
  const contractAddress = state?.address ?? rental.contractAddress ?? '';

  // The contract's own number, re-read while the modal is open (a new overdue day can start
  // between opening it and confirming).
  const { data: quote, isLoading: isQuoting, error: quoteError } = useRentalTopUpQuote(rental.id, visible);

  const ledger = depositLedgerOf(state);
  const toBig = (value: string | undefined): bigint => {
    try {
      return value ? BigInt(value) : 0n;
    } catch {
      return 0n;
    }
  };

  const topUpAmount = toBig(quote?.maxTopUp);
  const debtPart = toBig(quote?.outstandingOverdue);
  const depositPart = toBig(quote?.depositGap);
  const format = (value: bigint) => `${formatBaseUnits(value, token.decimals)} ${token.symbol}`;
  const isProcessing = (isSubmitting || tx.isRunning) && !success;

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setError('');
    setSuccess(false);
    tx.reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (isProcessing) {
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      if (!contractAddress || state == null) {
        throw new Error('The escrow contract is not ready yet. Please try again shortly.');
      }
      if (topUpAmount <= 0n) {
        throw new Error('Nothing to pay right now — your deposit is full and no overdue time is owed.');
      }

      const paymentToken = await readRentalPaymentToken(walletUI, contractAddress, chain?.chainId);

      const done = await tx.run(
        (onProgress) =>
          walletUI.callContract({
            contractAddress,
            signature: 'topUpDeposit(uint256)',
            args: [topUpAmount.toString()],
            chainId: chain?.chainId,
            approve: { tokenAddress: paymentToken, spender: contractAddress, amount: topUpAmount.toString() },
            onProgress,
          }),
        'Transaction failed. Please try again.'
      );

      if (done) {
        setSuccess(true);
        onToppedUp?.();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={
        success ? (
          <CheckCircle size={64} color="white" weight="fill" />
        ) : (
          <BankIcon size={64} color="white" weight="fill" />
        )
      }
      header={success ? 'Deposit topped up' : 'Top up your deposit'}
      buttonText={
        success ? 'Close' : isProcessing ? 'Processing…' : isQuoting ? 'Checking the amount…' : 'Approve & top up'
      }
      onSubmit={success ? handleClose : handleConfirm}
      disabled={isProcessing || (!success && (isQuoting || topUpAmount <= 0n))}
    >
      <Stack gap={3} width="100%">
        {!success ? (
          <>
            <Stack gap={1} className={styles.info}>
              <Row label="Deposit now" value={format(ledger.remainingDeposit)} />
              <Row label="Original deposit" value={format(ledger.depositAmount)} />
              {debtPart > 0n ? (
                <>
                  <Row label="Owed for days past the end date" value={format(debtPart)} />
                  <Row label="Back into your deposit" value={format(depositPart)} />
                </>
              ) : null}
              <Row label="You pay now" value={format(topUpAmount)} />
            </Stack>

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  Why the amount is fixed
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                The escrow accepts exactly <b>{format(topUpAmount)}</b> right now and rejects anything larger.{' '}
                {debtPart > 0n
                  ? 'Of that, the amount owed for days past the end date goes to the dealer, and the rest refills your deposit. '
                  : 'It refills your deposit to its original amount. '}
                Two prompts will appear: approve that exact amount (it is the payment, not a gas fee, so do not lower
                it), then confirm. Once the deposit is full and nothing is owed you can extend the rental again and go
                back to paying your own daily rate instead of the rate charged past the end date.
              </Typography>
            </Stack>
          </>
        ) : (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Your deposit is full again. You can now request an extension.
            </Typography>
          </Stack>
        )}

        <TxProgress
          state={tx.state}
          done={success}
          error={tx.error}
          // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
          connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
          explorerTxUrl={chain?.explorerTxUrl}
          onRetry={() => {
            tx.reset();
            handleConfirm();
          }}
        />

        {!success && quoteError ? (
          <Typography color="white" variant="small" className={styles.error}>
            Could not read the amount from the escrow. Please close and try again.
          </Typography>
        ) : null}

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small">
      {value}
    </Typography>
  </Stack>
);
